import { useEffect, useRef, useState } from "preact/hooks";
import { batch } from "@preact/signals";
import { ensureFacets } from "../../data/loader";
import { fetchPieces } from "../../data/api";
import { searchCities, searchComposers, searchOrgs } from "../../data/search-index";
import {
  selectedComposerId,
  selectedOrgId,
  setCityFilter,
  setPieceFilter,
} from "../../state/store";
import { countryDisplayName } from "../../utils/format";
import type { City, Composer, Org, Piece } from "../../types";

type Hit =
  | { kind: "composer"; item: Composer }
  | { kind: "org"; item: Org }
  | { kind: "city"; item: City }
  | { kind: "piece"; item: Piece };

const KIND_LABEL: Record<Hit["kind"], string> = {
  composer: "Composer",
  org: "Organization",
  city: "City",
  piece: "Piece",
};

/** One search box over everything that can narrow the slice: composers, organizations and
 *  cities from the in-browser index, pieces from the server. Picking a hit drills — the
 *  same as tapping it in a panel. Rendered as a plain listbox under the input (no floating
 *  positioning), which is what works on a phone keyboard. */
export function ExploreSearch({ onPick }: { onPick: () => void }) {
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<Hit[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const token = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setHits([]);
      return;
    }
    const mine = ++token.current;
    let cancelled = false;
    const controller = new AbortController();
    // Local hits first (instant once the facet files are in), pieces trail by a round trip.
    ensureFacets()
      .then(() => {
        if (cancelled || mine !== token.current) return;
        const local: Hit[] = [
          ...searchComposers(q, 5).map((item) => ({ kind: "composer", item }) as Hit),
          ...searchOrgs(q, 4).map((item) => ({ kind: "org", item }) as Hit),
          ...searchCities(q, 4).map((item) => ({ kind: "city", item }) as Hit),
        ];
        setHits(local);
        setActive(local.length ? 0 : -1);
      })
      .catch(() => {});
    const handle = setTimeout(() => {
      fetchPieces(q, { composer: selectedComposerId.peek(), limit: 5, signal: controller.signal })
        .then((pieces) => {
          if (cancelled || mine !== token.current) return;
          setHits((prev) => [...prev.filter((h) => h.kind !== "piece"), ...pieces.map((item) => ({ kind: "piece", item }) as Hit)]);
        })
        .catch(() => {});
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
      controller.abort();
    };
  }, [query]);

  function pick(h: Hit) {
    switch (h.kind) {
      case "composer":
        // Searching for a composer means "take me to the composer" — a piece filter left in
        // place would keep the page on the piece (its composer is already implied).
        batch(() => {
          setPieceFilter(null);
          selectedComposerId.value = h.item.id;
        });
        break;
      case "org":
        selectedOrgId.value = h.item.id;
        break;
      case "city":
        setCityFilter(h.item.id, "manual");
        break;
      case "piece":
        setPieceFilter(h.item);
        break;
    }
    setQuery("");
    setHits([]);
    setOpen(false);
    inputRef.current?.blur();
    onPick();
  }

  function onKey(e: KeyboardEvent) {
    if (!hits.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => (a + 1) % hits.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => (a - 1 + hits.length) % hits.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      pick(hits[Math.max(0, active)]);
    } else if (e.key === "Escape") {
      e.preventDefault(); // a type=search input would also wipe the text
      setOpen(false);
      inputRef.current?.blur();
    }
  }

  const showList = open && query.trim().length > 0;

  return (
    <div class="xp-search">
      <input
        ref={inputRef}
        class="xp-search-input"
        type="search"
        placeholder="Composer, piece, organization or city…"
        aria-label="Search composers, pieces, organizations and cities"
        value={query}
        autocomplete="off"
        enterkeyhint="search"
        role="combobox"
        aria-expanded={showList}
        aria-controls="xp-search-list"
        onInput={(e) => {
          setQuery((e.target as HTMLInputElement).value);
          setOpen(true);
        }}
        onFocus={() => {
          setOpen(true);
          void ensureFacets().catch(() => {});
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKey}
      />
      {showList && (
        <ul class="xp-search-list" role="listbox" id="xp-search-list">
          {hits.length === 0 && <li class="xp-search-empty">Searching…</li>}
          {hits.map((h, i) => (
            <li
              key={`${h.kind}-${h.kind === "piece" ? h.item.key : h.item.id}`}
              role="option"
              aria-selected={i === active}
              class={`xp-search-hit${i === active ? " active" : ""}`}
              onMouseDown={(e) => {
                e.preventDefault(); // keep the input's blur from closing before the click lands
                pick(h);
              }}
            >
              <span class="xp-search-kind">{KIND_LABEL[h.kind]}</span>
              <span class="xp-search-name">
                {h.kind === "piece" && h.item.composer ? `${h.item.composer}: ` : ""}
                {h.kind === "city" ? `${h.item.name}, ${countryDisplayName(h.item.country_code, h.item.country_name)}` : h.kind === "piece" ? h.item.label : h.item.name}
              </span>
              <span class="xp-search-count">{h.item.count.toLocaleString()}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
